// src/components/Sidebar.tsx
import React, { useState, useEffect } from 'react';
import { Link } from 'react-router-dom';
import { ChevronRight } from 'lucide-react';

interface Category {
  id: number;
  name: string;
  subCategories?: Category[];
}

const Sidebar: React.FC = () => {
  const [mainCategories, setMainCategories] = useState<Category[]>([]);
  const [activeCategory, setActiveCategory] = useState<Category | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    const fetchCategories = async () => {
      setLoading(true);
      try {
        const response = await fetch('/nodejs/api/categories');
        if (!response.ok) throw new Error('Failed');
        const data: Category[] = await response.json();
        setMainCategories(data);
      } catch (err) {
        setError('Error');
      } finally {
        setLoading(false);
      }
    };
    fetchCategories();
  }, []);

  if (loading) return <aside className="w-56 bg-white shadow-lg p-5 h-[450px]">Loading...</aside>;
  if (error) return <aside className="w-56 bg-white shadow-lg p-5 text-red-500">Error</aside>;

  const VISIBLE_LIMIT = 10; 
  const visibleCategories = mainCategories.slice(0, VISIBLE_LIMIT);

  return (
    // CHANGE 1: 'onMouseLeave' yahan move kiya taaki poore sidebar se bahar jane par hi close ho
    <aside 
      className="w-56 bg-white shadow-2xl p-4 min-h-[450px] h-full relative flex flex-col z-20"
      onMouseLeave={() => setActiveCategory(null)}
    >
      <h2 className="font-bold text-2xl mb-4 text-blue-950 flex items-center gap-2">
        Categories
      </h2>

      <ul className="space-y-1 flex-1">
        {visibleCategories.map((category) => (
          // CHANGE 2: Yahan se popup wala div hata diya hai
          <div
            key={category.id}
            onMouseEnter={() => setActiveCategory(category)}
            className="relative"
          >
            <li className="relative">
              <Link
                to={category.subCategories?.length ? '#' : `/categories/${category.id}`}
                className={`flex justify-between items-center px-3 py-2.5 text-bold text-gray-800 rounded-md transition-colors duration-200 
                  hover:bg-[#0f6f5c]/10 hover:text-[#0f6f5c]
                  ${activeCategory?.id === category.id ? 'bg-[#0f6f5c]/10 text-[#0f6f5c]' : ''}`
                }
              >
                <span className="truncate text-sm font-medium">{category.name}</span>
                {category.subCategories && category.subCategories.length > 0 && (
                    <ChevronRight size={16} className="text-gray-400" />
                )}
              </Link>
            </li>
          </div>
        ))}
      </ul>

      {/* CHANGE 3: Popup Menu ko loop se BAHAR nikal diya. Ab ye hamesha Top par fix rahega */}
      {activeCategory &&
        activeCategory.subCategories &&
        activeCategory.subCategories.length > 0 && (
          <div
            className="absolute left-full top-0 ml-2 w-auto bg-white border border-gray-200 rounded-md shadow-lg py-4 px-6 min-w-[600px] z-50 h-auto min-h-[450px]"
            style={{ marginTop: '0px' }} // Top align karne ke liye
          >
            <h3 className="font-bold text-lg mb-4 text-gray-800 border-b border-gray-200 pb-2">
              {activeCategory.name}
            </h3>
            <div className="mt-2 grid grid-cols-3 gap-x-8">
              {activeCategory.subCategories.map((subCat) => (
                <div key={subCat.id} className="mb-4 break-inside-avoid">
                  <h4 className="font-semibold text-gray-800 border-b border-gray-200 pb-1 mb-2">
                    {subCat.name}
                  </h4>
                  <ul className="space-y-1">
                    {subCat.subCategories?.slice(0, 5).map((nestedSubCat) => (
                      <li key={nestedSubCat.id}>
                        <Link
                          to={`/categories/${nestedSubCat.id}`}
                          className="block px-2 py-1 text-sm text-gray-600 hover:bg-[#0f6f5c]/10 hover:text-[#0f6f5c] rounded-md transition-colors duration-200"
                        >
                          {nestedSubCat.name}
                        </Link>
                      </li>
                    ))}
                  </ul>
                </div>
              ))}
            </div>
          </div>
      )}

      <div className="mt-auto pt-2 border-t border-gray-100 relative z-10">
        <Link
            to="/all-categories"
            className="flex items-center justify-center w-full px-3 py-2.5 text-[#0f6f5c] font-semibold hover:bg-[#0f6f5c]/10 rounded-md transition text-sm"
        >
            View All Categories
        </Link>
      </div>
    </aside>
  );
};

export default Sidebar;